import React from "react";
import { Box, Button, Typography } from "@mui/material";
import type { ConfirmChildLinkResult, WontSendRow } from "@services/facilityNetworkLink";
import { downloadNpiCsv } from "@services/facilityNetworkLink";
import successCheckIcon from "@assets/facilityNetwork/ac-success-check.svg";
import networkChevronIcon from "@assets/facilityNetwork/ac-review-right.svg";
import warningChevronIcon from "@assets/facilityNetwork/ac-warning-chevron.svg";
import csvChevronIcon from "@assets/facilityNetwork/ac-csv-right.svg";
import { formatConfirmOutcomeLine } from "./addChildFacilityReviewUtils";
import FacilityNetworkFigmaIcon from "./FacilityNetworkFigmaIcon";
import { FN } from "./facilityNetworkTheme";

type Props = {
  result: ConfirmChildLinkResult;
  wontSendRows?: WontSendRow[];
  onGoToNetwork: () => void;
  onGoToLinkRequests: () => void;
};

const CARD_SHADOW = "1px 1px 4px 0px rgba(98,102,104,0.05), -1px -1px 4px 0px rgba(98,102,104,0.05)";
const GREEN_BG = "#e6f4ea";
const GREEN_STROKE = "#a8dab5";
const GREEN_TEXT = "#137333";
const WARNING_BG = "#fff4e5";
const WARNING_STROKE = "#fabc89";
const WARNING_TEXT = "#8b4409";
const GRAY_BG = "#f3f2f1";
const GRAY_STROKE = "#c8c6c4";
const GRAY_TEXT = "#605e5c";

export default function NpiLinkSuccessPanel({
  result,
  wontSendRows = [],
  onGoToNetwork,
  onGoToLinkRequests,
}: Props) {
  const wontSendList = wontSendRows.length > 0 ? wontSendRows : (result.wont_send_rows || []);
  const wontSendCount = Math.max(result.invalid_skipped, wontSendList.length);
  const showSent = result.link_requests_sent > 0;
  const showSaved = result.action_needed_saved > 0;
  const showWontSend = wontSendCount > 0;
  const visibleCardCount = [showSent, showSaved, showWontSend].filter(Boolean).length;

  const handleDownloadWontSend = () => {
    downloadNpiCsv(
      "wont-send-facilities.csv",
      ["Identifier", "Reason", "Status"],
      wontSendList.map((row) => [row.identifier, row.reason, row.status || "ineligible"]),
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        width: "100%",
        pt: 2.5,
        pb: 10,
        px: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "64px",
            bgcolor: FN.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1,
          }}
        >
          <FacilityNetworkFigmaIcon src={successCheckIcon} size={29} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 24,
              fontWeight: 600,
              lineHeight: "32px",
              letterSpacing: "-0.06px",
              color: FN.text,
              textAlign: "center",
            }}
          >
            Review complete
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 16,
              fontWeight: 600,
              lineHeight: "22px",
              color: FN.muted,
              textAlign: "center",
            }}
          >
            {formatConfirmOutcomeLine(
              result.link_requests_sent,
              result.action_needed_saved,
              wontSendCount,
            )}
          </Typography>
          {showSaved ? (
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: "20px",
                color: WARNING_TEXT,
                textAlign: "center",
              }}
            >
              Add emails in the Requests tab to send invites
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="contained"
          onClick={onGoToNetwork}
          endIcon={<FacilityNetworkFigmaIcon src={networkChevronIcon} size={12} />}
          sx={{
            textTransform: "none",
            minHeight: 28,
            height: 28,
            px: 2,
            py: 0.75,
            borderRadius: "4px",
            bgcolor: FN.primary,
            color: "#fff",
            fontFamily: FN.fontFamily,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: "16px",
            letterSpacing: "0.03px",
            boxShadow: "none",
            "& .MuiButton-endIcon": { ml: "4px", mr: 0 },
            "&:hover": { bgcolor: FN.pressed, boxShadow: "none" },
          }}
        >
          Go to my network
        </Button>
      </Box>

      {visibleCardCount > 0 ? (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: `repeat(${visibleCardCount}, minmax(0, 1fr))`,
          },
          gap: 2,
          width: "100%",
          maxWidth: 900,
        }}
      >
        {showSent ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: "#fff",
            border: `1px solid ${GREEN_STROKE}`,
            borderRadius: "4px",
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          }}
        >
          <Box sx={{ bgcolor: GREEN_BG, borderBottom: `1px solid ${GREEN_STROKE}`, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: GREEN_TEXT,
                }}
              >
                Sent
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 20,
                  fontWeight: 600,
                  color: GREEN_TEXT,
                }}
              >
                {result.link_requests_sent}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, flex: 1, justifyContent: "space-between" }}>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent, lineHeight: "18px" }}>
              Link requests created in Sent for active facilities found in Unicomply.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={onGoToNetwork}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                fontFamily: FN.fontFamily,
                fontSize: 13,
                fontWeight: 500,
                color: FN.primary,
                textAlign: "left",
              }}
            >
              View in My Network
              <FacilityNetworkFigmaIcon src={networkChevronIcon} size={12} />
            </Box>
          </Box>
        </Box>
        ) : null}

        {showSaved ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: "#fff",
            border: `1px solid ${WARNING_STROKE}`,
            borderRadius: "4px",
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          }}
        >
          <Box sx={{ bgcolor: WARNING_BG, borderBottom: `1px solid ${WARNING_STROKE}`, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: WARNING_TEXT,
                }}
              >
                Saved for follow-up
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 20,
                  fontWeight: 600,
                  color: WARNING_TEXT,
                }}
              >
                {result.action_needed_saved}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, flex: 1, justifyContent: "space-between" }}>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent, lineHeight: "18px" }}>
              Saved as Invite needed placeholders in Requests tab.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={onGoToLinkRequests}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                border: "none",
                bgcolor: "transparent",
                cursor: "pointer",
                p: 0,
                fontFamily: FN.fontFamily,
                fontSize: 13,
                fontWeight: 500,
                color: WARNING_TEXT,
                textAlign: "left",
              }}
            >
              Go to Requests tab
              <FacilityNetworkFigmaIcon src={warningChevronIcon} size={12} />
            </Box>
          </Box>
        </Box>
        ) : null}

        {showWontSend ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            bgcolor: "#fff",
            border: `1px solid ${GRAY_STROKE}`,
            borderRadius: "4px",
            boxShadow: CARD_SHADOW,
            overflow: "hidden",
          }}
        >
          <Box sx={{ bgcolor: GRAY_BG, borderBottom: `1px solid ${GRAY_STROKE}`, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "20px",
                  color: GRAY_TEXT,
                }}
              >
                Won't send
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 20,
                  fontWeight: 600,
                  color: GRAY_TEXT,
                }}
              >
                {wontSendCount}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, flex: 1, justifyContent: "space-between" }}>
            <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.figmaAccent, lineHeight: "18px" }}>
              Rows skipped due to duplicate, linked elsewhere, or invalid format.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={handleDownloadWontSend}
              disabled={wontSendCount === 0 || wontSendList.length === 0}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                border: "none",
                bgcolor: "transparent",
                cursor: wontSendCount === 0 || wontSendList.length === 0 ? "default" : "pointer",
                p: 0,
                fontFamily: FN.fontFamily,
                fontSize: 13,
                fontWeight: 500,
                color: FN.primary,
                opacity: wontSendCount === 0 || wontSendList.length === 0 ? 0.45 : 1,
                textAlign: "left",
              }}
            >
              Download skipped CSV
              <FacilityNetworkFigmaIcon src={csvChevronIcon} size={12} />
            </Box>
          </Box>
        </Box>
        ) : null}
      </Box>
      ) : null}
    </Box>
  );
}
